import { Pipe, PipeTransform, Inject } from '@angular/core';
import { Library, Protocol } from '@library';
import { LIBRARY } from '../library';

// The 3 protocols that carried the original broken `crop: "a" || "b" || ...` literal
// (genuinely intended to span multiple crops) get a fixed text label instead of a
// single-crop icon. Every other protocol keeps a representative crop icon, even if
// Crop.protocols[] incidentally lists it under more than one crop for unrelated reasons
// (e.g. apple_tree_screen_eval also appearing under quince) — per developer direction 2026-08-13.
// If a future protocol is intentionally given multi-crop scope, add it here — it would
// otherwise silently fall through to the single-icon branch below.
const MULTI_CROP_TEXT_OVERRIDES: Record<string, string> = {
    stone_tree_screen_eval: 'stone',
    stone_harvest_screen_eval: 'stone',
    harvest_impression_eval: 'Overall',
};

export interface ProtocolCropDisplay {
    type: 'icon' | 'text';
    value: string;
}

@Pipe({
  name: 'protocolCropDisplay'
})
export class ProtocolCropDisplayPipe implements PipeTransform {

    constructor(@Inject(LIBRARY) private _evalLib: Library) {}

    transform(protocol: Pick<Protocol, 'id' | 'customCropId'>): ProtocolCropDisplay {

        if (!protocol || !protocol.id) return { type: 'text', value: '' };

        // Custom protocols carry their own single cropId (CustomProtocolService.toProtocol()) —
        // they're never in the static library's Crop.protocols[], so getCropsByProtocol() below
        // would never resolve them. Their cropId is unrelated to the removed broken-literal bug.
        if (protocol.customCropId) {
            return { type: 'icon', value: protocol.customCropId };
        }

        if (MULTI_CROP_TEXT_OVERRIDES[protocol.id]) {
            return { type: 'text', value: MULTI_CROP_TEXT_OVERRIDES[protocol.id] };
        }

        const cropIds = this._evalLib.getCropsByProtocol(protocol.id);

        if (cropIds.length === 0) {
            return { type: 'text', value: 'Unknown Crop' };
        }

        // First entry is a stable choice only because crops.ts declares crops in a fixed
        // order (e.g. apple before quince) — getCropsByProtocol() preserves that order.
        // Reordering crops.ts would change which icon an incidentally-multi-crop protocol shows.
        return { type: 'icon', value: cropIds[0] };
    }
}
